import { Fragment, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { applyFeeDiscount, formatPrice } from 'utils/price'
import { getDateString, getUserTimezone } from 'utils/time'

import BlockInfo from 'components/common/BlockInfo/BlockInfo'
import Button from 'components/common/Button/Button'
import { DecimalDTO } from 'models/misc'
import { Nullable } from 'models/helpers'
import { ProductType } from 'constants/product'
import { UpsellPayment } from '../UpsellPayment'
import { ZERO_DECIMAL } from 'constants/misc'
import { identifiableProduct } from 'utils/serialization/productIdentifier'
import { logger } from 'utils/logging'
import { mutationToAPIRequest } from 'utils/reactQuery/queryHelpers'
import styles from './VirtualVisitHostingInfo.module.sass'
import { useAuth0 } from 'utils/auth'
import { useExtendVirtualVisitHosting } from 'dataQueries/visual.query'
import { useGetVirtualVisitExtensionPrice, useUserMe } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @interface Props Input properties of VirtualVisitHostingInfo component
 */
interface Props {
  /** Date string of virtual visit hosting expiration date */
  hostingExpirationDate: string | null
  /** Is virtual visit hosting expired? */
  isExpired: boolean,
  /** ID of assignment */
  assignmentId: string
  /** Organization discount */
  discount?: DecimalDTO
}

/**
 * @component Shows info about virtual visit hosting expiration and allows client to extend/reactivate hosting
 * @example <VirtualVisitHostingInfo hostingExpirationDateString={dateString} isExpired={false} assignmentId={77678}>
 * */
export const VirtualVisitHostingInfo: React.FC<Props> = ({
  hostingExpirationDate,
  isExpired,
  assignmentId,
  discount = ZERO_DECIMAL
}) => {
  const { t } = useTranslation(['virtual_visit_info', 'gallery', 'gallery_staging_flow'])
  const { spawnErrorToast } = useSnackbar()
  const { roles, isAuthenticated } = useAuth0()
  const { userPropertyWorkspaceId } = useUserData()

  const { data: user } = useUserMe(isAuthenticated)

  const virtualExtensionPrice = useGetVirtualVisitExtensionPrice(assignmentId, userPropertyWorkspaceId, roles)
  const virtualExtensionPriceData = useMemo(() => virtualExtensionPrice.data?.data, [virtualExtensionPrice])

  const extendVirtualVisitMutation = useExtendVirtualVisitHosting()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const extendVirtualVisitRequest = useMemo(() => mutationToAPIRequest(extendVirtualVisitMutation), [extendVirtualVisitMutation.status, extendVirtualVisitMutation.error])

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const extensionPrice = useMemo(() => virtualExtensionPriceData?.fee, [virtualExtensionPriceData])
  const extensionProductId = useMemo(() => virtualExtensionPriceData?.productId, [virtualExtensionPriceData])
  const currency = useMemo(() => extensionPrice?.currency, [extensionPrice])
  const extensionTotal = useMemo(() => !!extensionPrice ? applyFeeDiscount({ ...extensionPrice, discountPercentage: discount }) : undefined, [extensionPrice, discount])
  const expirationDateString = useMemo(() => !!hostingExpirationDate ? getDateString(hostingExpirationDate, { timezone: getUserTimezone(user) }) : '', [user, hostingExpirationDate])

  /**
   * Extends the hosting, identifying the picked payment option by its Product2 code — the only form
   * the extension wire accepts. The payment intent already exists here, so an option without a code
   * aborts loudly rather than sending a request the API would reject.
   */
  const handleExtendHosting = useCallback((paymentIntentId: string | undefined, billingProductCode: Nullable<string>) => {
    const billingProduct = identifiableProduct(billingProductCode)

    if (!billingProduct) {
      spawnErrorToast(t('gallery_staging_flow:upsell_abort_error'))
      logger.error('Virtual visit extension not submitted - the billing product carries no product code', undefined, {
        context: { component: 'VirtualVisitHostingInfo', action: 'handleExtendHosting', metadata: { assignmentId } },
      })

      return
    }

    extendVirtualVisitMutation.mutate({ assignmentId, workspaceId: userPropertyWorkspaceId, billingProduct, paymentIntentId })
  }, [assignmentId, extendVirtualVisitMutation, spawnErrorToast, t, userPropertyWorkspaceId])

  return (
    <Fragment>

      <BlockInfo className={styles.hostingInfo}>

        {isExpired
          ? <Fragment>
            <h4 className={styles.title}>
              {t('reactivate_info_title')}
            </h4>
            <p>
              {/* Using Trans to render tags like <strong> and <br /> */}
              <Trans t={t} i18nKey="reactivate_info_body" values={{ price: formatPrice(extensionPrice, currency) }}>
                <span></span>
              </Trans>
            </p>
          </Fragment>
          : <Fragment>
            <h4 className={styles.title}>
              {t('extend_info_title', { expirationDate: expirationDateString })}
            </h4>
            <p>
              {/* Using Trans to render tags like <strong> and <br /> */}
              <Trans t={t} i18nKey="extend_info_body" values={{ price: formatPrice(extensionPrice, currency) }}>
                <span></span>
              </Trans>
            </p>
          </Fragment>
        }

        <div className={styles.actionWrap}>
          <Button onClick={() => setIsModalOpen(true)}>
            {t(isExpired ? 'reactivate_cta' : 'extend_cta')}
          </Button>
        </div>

      </BlockInfo>

      <UpsellPayment
        assignmentId={assignmentId}
        productId={extensionProductId}
        productQuantity={1}
        total={extensionTotal}
        isOpen={isModalOpen}
        beforeConfirmAction={handleExtendHosting}
        beforeConfirmActionState={extendVirtualVisitRequest?.state}
        beforeConfirmActionMutation={extendVirtualVisitMutation}
        onClose={() => setIsModalOpen(false)}
        afterClosed={() => extendVirtualVisitMutation.reset()}
        successTitle={t(isExpired ? 'reactivate_success' : 'extend_success')}
        productType={ProductType.VIRTUAL_VISIT}
      >
        {/* Using Trans to render tags like <strong> and <br /> */}
        <Trans t={t} i18nKey={isExpired ? 'reactivate_confirm_text' : 'extend_confirm_text'} values={{ price: formatPrice(extensionTotal, currency) }}>
          <span></span>
        </Trans>
      </UpsellPayment>

    </Fragment>
  )
}
